import {
  AccountInfo,
  EndSessionRequest,
  IPublicClientApplication,
} from "@azure/msal-browser";

type LogoutMsalInstance = Pick<
  IPublicClientApplication,
  "getActiveAccount" | "getAllAccounts" | "logoutRedirect"
>;

export type LogoutWithMicrosoftArgs = {
  instance: LogoutMsalInstance;
  clearApolloStore?: () => Promise<unknown>;
  postLogoutRedirectUri?: string;
};

type LogoutLocallyToLoginArgs = {
  instance: LogoutMsalInstance;
  clearApolloStore?: () => Promise<unknown>;
  redirectPath?: string;
  navigate?: (path: string) => void;
};

const DEFAULT_LOGIN_PATH = "/login";
const DEFAULT_POST_LOGOUT_REDIRECT_URI = DEFAULT_LOGIN_PATH;
let logoutInFlight: Promise<void> | null = null;

function resolveLogoutAccount(
  instance: LogoutMsalInstance
): AccountInfo | null {
  const activeAccount = instance.getActiveAccount();
  if (activeAccount) return activeAccount;

  const [firstAccount] = instance.getAllAccounts();
  return firstAccount ?? null;
}

function getLoginHintFromAccount(account: AccountInfo | null): string | null {
  const loginHint = account?.idTokenClaims?.login_hint;
  if (typeof loginHint !== "string" || loginHint.trim().length === 0) {
    return null;
  }

  return loginHint;
}

export function buildPromptlessLogoutRequest(
  instance: LogoutMsalInstance,
  postLogoutRedirectUri: string | null = DEFAULT_POST_LOGOUT_REDIRECT_URI
): EndSessionRequest {
  const account = resolveLogoutAccount(instance);
  const logoutRequest: EndSessionRequest = {
    postLogoutRedirectUri: postLogoutRedirectUri,
  };

  if (account) {
    logoutRequest.account = account;
  }

  const logoutHint = getLoginHintFromAccount(account);
  if (logoutHint) {
    logoutRequest.logoutHint = logoutHint;
  }

  return logoutRequest;
}

async function clearLogoutState(
  clearApolloStore?: () => Promise<unknown>
): Promise<void> {
  localStorage.removeItem("userID");

  if (clearApolloStore) {
    await clearApolloStore();
  }
}

function runLogout(action: () => Promise<void>): Promise<void> {
  if (logoutInFlight) {
    return logoutInFlight;
  }

  logoutInFlight = action().finally(() => {
    logoutInFlight = null;
  });

  return logoutInFlight;
}

export async function logoutWithMicrosoft({
  clearApolloStore,
  instance,
  postLogoutRedirectUri = DEFAULT_POST_LOGOUT_REDIRECT_URI,
}: LogoutWithMicrosoftArgs): Promise<void> {
  await runLogout(async () => {
    await clearLogoutState(clearApolloStore);

    await instance.logoutRedirect(
      buildPromptlessLogoutRequest(instance, postLogoutRedirectUri)
    );
  });
}

export async function logoutLocallyToLogin({
  clearApolloStore,
  instance,
  redirectPath = DEFAULT_LOGIN_PATH,
  navigate = (path) => window.location.replace(path),
}: LogoutLocallyToLoginArgs): Promise<void> {
  await runLogout(async () => {
    await clearLogoutState(clearApolloStore);

    await instance.logoutRedirect({
      ...buildPromptlessLogoutRequest(instance, null),
      onRedirectNavigate: () => false,
    });

    navigate(redirectPath);
  });
}
